// Résumés textuels de la recherche courante (trajet / dates / passagers /
// véhicule) — SOURCE UNIQUE pour les cartes de Search.vue et le bandeau
// SearchRecap.vue (Results.vue), pour ne jamais diverger entre les deux.
import { computed } from 'vue'
import { useStore } from '../store'
import { useI18n } from '../i18n'
import { countPassengers, countPets } from '../../../shared/rules'
import { Direction } from '../../../shared/types/enums'
import type { RequestItem } from '../../../shared/types'

export function useSearchSummary() {
  const store = useStore()
  const { t, locale } = useI18n()

  function findDate(field: 'out_at' | 'ret_at'): string | null {
    for (const items of Object.values(store.search.request)) {
      for (const item of items as RequestItem[]) {
        if (item[field]) return item[field] as string
      }
    }
    return null
  }

  function formatShort(iso: string): string {
    const [y, m, d] = iso.split('-').map(Number)
    const date = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1)
    return date.toLocaleDateString(locale.value, { day: '2-digit', month: 'short' })
  }

  function capitalize(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
  }

  const journeySummary = computed(() => {
    const primary = store.search.journeys.find((j) => j.direction === Direction.Outward) ?? store.search.journeys[0]
    if (!primary) return t('search.journeyPlaceholder')
    const from = primary.origin?.port_name ?? '—'
    const to = primary.destination?.port_name ?? '—'
    const arrow = store.search.direction === Direction.Return ? '⇌' : '→'
    return `${from} ${arrow} ${to}`
  })

  const datesSummary = computed(() => {
    const out = findDate('out_at')
    const ret = findDate('ret_at')
    const isReturn = store.search.direction === Direction.Return
    if (!out) return t('search.datesPlaceholder')
    if (isReturn) {
      if (!ret) return `${formatShort(out)} · ${t('search.pickReturn')}`
      return `${formatShort(out)} → ${formatShort(ret)}`
    }
    return formatShort(out)
  })

  const passengersSummary = computed(() => {
    const c = countPassengers(store.search.passengers)
    const parts: string[] = []
    if (c.adults) parts.push(`${c.adults} ${t('passengers.adults').toLowerCase()}`)
    if (c.children) parts.push(`${c.children} ${t('passengers.children').toLowerCase()}`)
    if (c.babies) parts.push(`${c.babies} ${t('passengers.babies').toLowerCase()}`)
    const petTotal = countPets(store.search.pets)
    if (petTotal > 0) parts.push(`${petTotal} ${t('passengers.petsSection').toLowerCase()}`)
    return parts.join(', ')
  })

  // Retombe sur `search.vehicleNone` quand rien n'est choisi — identique au
  // comportement historique de Search.vue (call sites qui veulent OMETTRE le
  // segment plutôt que d'afficher "Aucun véhicule" testent `search.vehicle.type`
  // eux-mêmes, cf. SearchRecap.vue).
  const vehicleSummary = computed(() => {
    const v = store.search.vehicle
    if (!v.type) return t('search.vehicleNone')
    const typeLabel = t(`vehicle.type${capitalize(v.type)}`)
    if (v.brand_name && v.model) return `${typeLabel} · ${v.brand_name} ${v.model}`
    if (v.brand_name) return `${typeLabel} · ${v.brand_name}`
    if (typeof v.identification === 'string' && v.identification) return `${typeLabel} · ${v.identification}`
    return typeLabel
  })

  return { journeySummary, datesSummary, passengersSummary, vehicleSummary }
}
